import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import { CompanyProvider } from "./context/CompanyContext";
import { Layout } from "./components/Layout";
import Login from "./pages/Login";
import MailPage from "./pages/MailPage";
import TasksPage from "./pages/TasksPage";
import ChatPage from "./pages/ChatPage";
import TicketsPage from "./pages/TicketsPage";
import ContactsPage from "./pages/ContactsPage";
import CompaniesPage from "./pages/CompaniesPage";
import ReportsPage from "./pages/ReportsPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import ProfilePage from "./pages/ProfilePage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import ServicesPage from "./pages/ServicesPage";
import NotesPage from "./pages/NotesPage";
import HomePage from "./pages/HomePage";
import IntegrationPage from "./pages/IntegrationPage";
import ClientsPage from "./pages/ClientsPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import VaultPage from "./pages/VaultPage";
import MembersPage from "./pages/MembersPage";
import BillingComingSoon from "./pages/BillingComingSoon";
import AdminPage from "./pages/AdminPage";
import CataloguePage from "./pages/CataloguePage";
import GoogleCallback from "./pages/GoogleCallback";
import PublicValidation from "./pages/PublicValidation";
import DevisPage from "./pages/DevisPage";
import PublicQuote from "./pages/PublicQuote";
import AbonnementsPage from "./pages/AbonnementsPage";
import BrandedPortalLogin from "./pages/BrandedPortalLogin";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="font-mono-label text-xs uppercase tracking-[0.3em] text-zinc-400 animate-pulse">
          FlowDesk — chargement…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portal" replace />;
  return (
    <RealtimeProvider>
      <CompanyProvider>
        <Layout>{children}</Layout>
      </CompanyProvider>
    </RealtimeProvider>
  );
}

function ClientProtected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="font-mono-label text-xs uppercase tracking-[0.3em] text-zinc-400 animate-pulse">
          FlowDesk — chargement…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <RealtimeProvider>
      <CompanyProvider>{children}</CompanyProvider>
    </RealtimeProvider>
  );
}

function App() {
  if (typeof document !== "undefined" && localStorage.getItem("fd_theme") === "light") {
    document.documentElement.classList.add("light");
  }
  return (
    <div className="App">
      <Toaster position="top-center" richColors closeButton duration={2500} />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/google-callback" element={<GoogleCallback />} />
            <Route path="/validate/:token" element={<PublicValidation />} />
            <Route path="/q/:token" element={<PublicQuote />} />
            <Route path="/" element={<Protected><HomePage /></Protected>} />
            <Route path="/mail" element={<Protected><MailPage /></Protected>} />
            <Route path="/tickets" element={<Protected><TicketsPage /></Protected>} />
            <Route path="/tasks" element={<Protected><TasksPage /></Protected>} />
            <Route path="/chat" element={<Protected><ChatPage /></Protected>} />
            <Route path="/contacts" element={<Protected><ContactsPage /></Protected>} />
            <Route path="/companies" element={<Protected><CompaniesPage /></Protected>} />
            <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
            <Route path="/kb" element={<Protected><KnowledgeBasePage /></Protected>} />
            <Route path="/suivi" element={<Protected><ServicesPage /></Protected>} />
            <Route path="/notes" element={<Protected><NotesPage /></Protected>} />
            <Route path="/integration" element={<Protected><IntegrationPage /></Protected>} />
            <Route path="/clients" element={<Protected><ClientsPage /></Protected>} />
            <Route path="/clients/:email" element={<Protected><ClientProfilePage /></Protected>} />
            <Route path="/vault" element={<Protected><VaultPage /></Protected>} />
            <Route path="/members" element={<Protected><MembersPage /></Protected>} />
            <Route path="/catalogue" element={<Protected><CataloguePage /></Protected>} />
            <Route path="/devis" element={<Protected><DevisPage /></Protected>} />
            <Route path="/factures" element={<Protected><BillingComingSoon /></Protected>} />
            <Route path="/abonnements" element={<Protected><AbonnementsPage /></Protected>} />
            <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
            <Route path="/portal" element={<ClientProtected><ClientPortalPage /></ClientProtected>} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/:slug" element={<BrandedPortalLogin />} />
            <Route path="*" element={<Navigate to="/mail" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
